<template>
  <div class="container">
    <div v-if="!properties.loadingStatus.isLoading" class="columns">
      <div class="column order-services-view--main">
        <div class="order-services-view--select-address">
          <LsMultiAddressSelect
            :properties="propertiesInArray"
            :selected-property-id="property_id"
            @input="setAddress"
          />
        </div>
        <div v-if="canOrder !== 'canOrder'" class="order-servicesview--order-wrapper">
          <div v-if="canOrder === 'inactive' && selectedProperty" test-id="inactive">
            <LsReactivateCard :property="selectedProperty" />
          </div>
          <div v-else-if="canOrder === 'unavailable'" test-id="unavailable">
            <LsInfoCard>
              <p>
                Service is not currently available in your area. We're adding availability all the
                time though so check back soon!
              </p>
            </LsInfoCard>
          </div>
        </div>
        <div v-else>
          <h1 class="heading-text">Which service would you like to add to your schedule?</h1>
          <BLoading :active.sync="orderServices.loadingStatus.isLoading" />
          <div v-if="!orderServices.loadingStatus.isLoading" class="services-grid" test-id="grid">
            <div
              v-for="({ name, service_id, str_id }, idx) in availableServices"
              :key="service_id"
              class="items"
              test-id="items"
            >
              <div
                test-id="service"
                :class="{
                  line: getBorder({ idx }),
                  'line-mobile': !screenSize.desktop,
                }"
              >
                <RouterLink
                  test-id="item"
                  :class="['item', { 'border-left': idx % 5 }]"
                  :to="getLink({ service_id, str_id })"
                >
                  <div v-if="str_id !== 'other'" class="image">
                    <img
                      test-id="img"
                      :src="require(`../../../assets/services/${str_id}.png`)"
                      :alt="name"
                    />
                  </div>

                  <div v-else class="other-circle"><span class="other-dots">•••</span></div>
                  <span class="name">{{ name }}</span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import _ from 'lodash';
import {
  PROPERTIES__PROPERTIES_IN_ARRAY,
  ORDER_SERVICES__GET_SERVICES,
  ORDER_SERVICES__AVAILABLE_SERVICES,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTY_ID_FOR_ORDER_SERVICES,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
} from '@/store/types';
import { LsMultiAddressSelect } from '@/components/common';
import { lsRouterHelpers } from 'ls-vue-common';
import LsInfoCard from '@/components/common/LsInfoCard/LsInfoCard.vue';
import LsReactivateCard from '../ServicesView/components/LsReactivateCard/LsReactivateCard.vue';

export default {
  name: 'OrderServicesView',
  components: {
    LsMultiAddressSelect,
    LsInfoCard,
    LsReactivateCard,
  },
  data() {
    return {
      property_id: _.toNumber(lsRouterHelpers.getParam(this.$route, 'property_id')),
    };
  },
  computed: {
    ...mapState(['orderServices', 'properties', 'screenSize', 'featureFlags']),
    ...mapGetters([
      ORDER_SERVICES__AVAILABLE_SERVICES,
      PROPERTIES__SELECTED_PROPERTY,
      PROPERTIES__PROPERTIES_IN_ARRAY,
      PROPERTIES__GET_PROPERTY_ID_FOR_ORDER_SERVICES,
    ]),
    availableServices() {
      return !this.doesPropertyExist
        ? []
        : this[ORDER_SERVICES__AVAILABLE_SERVICES](this.property_id);
    },
    doesPropertyExist() {
      return _.has(this.properties.propertiesById, this.property_id);
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    propertiesInArray() {
      return this[PROPERTIES__PROPERTIES_IN_ARRAY];
    },
    canOrder() {
      const property = this.selectedProperty;
      if (!_.get(property, 'is_active')) {
        return 'inactive';
      }
      if (!_.get(property, 'market_id')) {
        return 'unavailable';
      }
      return 'canOrder';
    },
  },
  watch: {
    $route(to) {
      this.property_id = _.toNumber(to.params.property_id);
      this.getBestPropertyId();
      this[ORDER_SERVICES__GET_SERVICES]({ property_id: this.property_id });
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();
    this.getBestPropertyId();

    this[ORDER_SERVICES__GET_SERVICES]({ property_id: this.property_id });

    if (this.canOrder === 'canOrder') {
      this[FEATURE_FLAGS__REGISTER_EXPOSURE]({
        flag: 'customer.manualquotes.questions.mulching',
      });
      this[FEATURE_FLAGS__REGISTER_EXPOSURE]({
        flag: 'customer.manualquotes.questions.cleanup',
      });
    }
  },
  methods: {
    ...mapActions([
      ORDER_SERVICES__GET_SERVICES,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      FEATURE_FLAGS__REGISTER_EXPOSURE,
    ]),
    getBestPropertyId() {
      this.property_id = this[PROPERTIES__GET_PROPERTY_ID_FOR_ORDER_SERVICES](this.property_id);
      this.setAddress(this.property_id);
    },
    setAddress(value) {
      this.$router.replace({
        params: {
          property_id: value,
        },
      });
    },
    getBorder({ idx }) {
      const totalItemCount = this.availableServices.length;
      let numberOfItemsNeedingBorder = totalItemCount - (totalItemCount % 5);

      if (totalItemCount % 5 === 0) {
        numberOfItemsNeedingBorder -= 5;
      }

      return idx < numberOfItemsNeedingBorder;
    },
    getLink({ service_id, str_id }) {
      if (str_id === 'fertilization' || str_id === 'aeration') {
        return {
          name: 'orderPricedServiceForm',
          params: { str_id },
        };
      }
      return {
        name: 'orderServiceForm',
        params: { service_id },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.order-services-view--main {
  padding-top: 100px;
}
.order-services-view--select-address {
  position: absolute;
  right: -10px;
  top: 20px;
  display: flex;
  justify-content: flex-end;
  background: $white;
  z-index: $z-index-multi-address-select;
}

.order-servicesview--order-wrapper {
  padding-top: 26px;
}

.heading-text {
  text-align: center;
  padding: 20px 0;
}

.services-grid {
  display: flex;
  flex-wrap: wrap;
  padding-top: 30px;
}

.line {
  border-bottom: 1px solid $primary-light;
}

.item {
  margin: 5px 0;
  height: 220px;
  width: 192px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background-color: $primary;
  }
  &:hover .name {
    color: $primary-light;
  }
}

.image {
  width: 87px;
  height: 87px;
}

.other-circle {
  height: 87px;
  width: 87px;
  background-color: $primary-light;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  .other-dots {
    font-size: 5.6rem;
    color: $primary;
    line-height: 8rem;
  }
}

.name {
  width: 100px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30%;
  padding-top: 30px;
  font-size: $size-4;
  color: $primary;
}

.border-left {
  border-left: 1px solid $primary-light;
}

@media (max-width: $mobile-max-width) {
  .order-services-view--select-address {
    width: 100%;
  }
  .ls-address-card {
    padding-bottom: 15px;
    border-bottom: 1px solid $primary-light;
  }

  .services-grid {
    flex-direction: column;
    width: 100%;
  }
  .line {
    border-bottom: none;
  }
  .line-mobile {
    border-top: 1px solid $primary-light;
  }

  .item {
    margin: 0;
    height: 80px;
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    &:hover {
      background-color: $white;
    }
    &:hover .name {
      color: $special-primary-hover;
    }
  }
  .image {
    width: 57px;
    height: 57px;
  }

  .other-circle {
    height: 57px;
    width: 57px;
    .other-dots {
      font-size: 3.6rem;
      color: $primary;
      line-height: 5.2rem;
    }
  }

  .name {
    width: auto;
    padding: 0;
  }

  .border-left {
    border-left: none;
  }
}

.themed-app {
  .order-services-view--select-address {
    background: $theme-color-white;
  }

  .line {
    border-bottom-color: $theme-color-primary-light;
  }

  .item {
    &:hover {
      background-color: $theme-color-primary;
    }
    &:hover .name {
      color: $theme-color-primary-light;
    }
  }

  .other-circle {
    background-color: $theme-color-primary-light;
    .other-dots {
      color: $theme-color-primary;
    }
  }

  .name {
    font-size: $theme-txt-body-lg;
    color: $theme-color-primary;
  }

  .border-left {
    border-left-color: $theme-color-primary-light;
  }

  @media (max-width: $mobile-max-width) {
    .ls-address-card {
      border-bottom-color: $theme-color-primary-light;
    }

    .line-mobile {
      border-top-color: $theme-color-primary-light;
    }

    .item {
      &:hover {
        background-color: $theme-color-white;
      }
      &:hover .name {
        color: rgba($theme-color-primary, 0.65);
      }
    }

    .other-circle {
      .other-dots {
        color: $theme-color-primary;
      }
    }
  }
}
</style>
